/**
 * Du dossier lu dans les pièces à la planification.
 *
 * La lecture des pièces (contacts.extraction_pieces, migration 069) et le
 * mail de planification (migration 068) contiennent, quand tout va bien,
 * les formations vendues, les journées, le formateur et les participants.
 * Cette fonction les met dans la forme des formulaires — et dit ce qui
 * MANQUE.
 *
 * Les deux sources se complètent : la feuille de présence nomme les
 * stagiaires et le formateur (nom, prénom, pas d'adresse), le mail du
 * client donne les adresses. Une même personne vue des deux côtés ne fait
 * qu'un participant, rapproché sur son nom.
 *
 * Utilisée par SessionsPanel (remplissage de la planification).
 */

import { participantsDepuisEmail } from './participantsDepuisEmail';

export const LIGNE_VIDE = {
  formationId: '',
  calendrierId: '',
  format: 'inter_presentiel',
  dureeJours: '',
  nbParticipants: 1,
  groupeLibelle: '',
  // Vide : la grille tarifaire. Un prix saisi vaut pour ce dossier seul.
  prixUnitaireHt: '',
};

export const JOURNEE_VIDE = {
  intitule: '',
  groupeLibelle: '',
  jours: [''],
  heureDebut: '09:00',
  heureFin: '17:00',
  modalite: 'presentiel',
  lieu: '',
  adresse: '',
  // Fiche formateur (migration 045) ou nom libre quand aucune fiche ne
  // correspond : la feuille de présence le nomme, on ne le ressaisit pas.
  formateurId: '',
  formateurs: '',
  // Son adresse : c'est elle qui reçoit le lien d'émargement et les rappels.
  formateurEmail: '',
};

/** La qualité lue dans un mail qui désigne celui qui anime, pas un élu. */
export const RE_QUALITE_FORMATEUR = /formateur|formatrice|intervenant/i;

/**
 * Sépare, dans une liste lue d'un mail, les stagiaires de celui qui anime
 * (ligne « Formateur : … »). Le formateur n'est jamais convoqué comme élu :
 * chaque écran qui colle un mail passe par ici avant d'ajouter des lignes.
 *
 * @returns {{ participants: Array, formateur: {nom, email, civilite}|null }}
 */
export function separerFormateur(lus) {
  const formateurs = lus.filter((p) => RE_QUALITE_FORMATEUR.test(p.qualite || ''));
  const f = formateurs[0] || null;
  return {
    participants: lus.filter((p) => !RE_QUALITE_FORMATEUR.test(p.qualite || '')),
    formateur: f
      ? {
          civilite: f.civilite || '',
          prenom: f.prenom || '',
          nom: f.nom || '',
          nomComplet: [f.prenom, f.nom].filter(Boolean).join(' '),
          email: f.email || '',
        }
      : null,
  };
}

export const PARTICIPANT_VIDE = {
  civilite: '',
  prenom: '',
  nom: '',
  societe: '',
  qualite: '',
  email: '',
};

const normaliser = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Clé d'une personne par son nom : les mots du prénom et du nom, triés.
 * « DUPONT Marie » sur la feuille et « Marie Dupont » dans le mail donnent
 * la même clé ; l'ordre et la casse ne comptent pas.
 */
const cleNom = (p) =>
  normaliser(`${p.prenom || ''} ${p.nom || ''}`)
    .split(' ')
    .filter(Boolean)
    .sort()
    .join(' ');

/** 'aaaa-mm-jj' d'une date reçue de l'API (chaîne ISO, instant, Date), en heure locale. */
export function jourLocal(valeur) {
  if (!valeur) return '';
  const s = String(valeur);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = valeur instanceof Date ? valeur : new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * La session du calendrier qu'une ligne INTER doit suivre : celle de sa
 * formation dont la période couvre un jour des journées saisies. C'est la
 * date qui désigne la session, on ne la demande pas deux fois.
 */
export function sessionCalendrierPour(ligne, journees, calendrier = []) {
  if (!/^inter/.test(String(ligne.format || '')) || !ligne.formationId) return null;
  const jours = (journees || []).flatMap((j) => j.jours || []).filter(Boolean);
  if (!jours.length) return null;
  return (
    calendrier.find((s) => {
      if (String(s.formation_id) !== String(ligne.formationId) || s.statut === 'annulee') return false;
      const debut = jourLocal(s.date_debut);
      const fin = jourLocal(s.date_fin) || debut;
      return jours.some((j) => j >= debut && j <= fin);
    }) || null
  );
}

/** Les lignes avec leur session du calendrier déduite des journées. */
export function rattacherSessions(lignes, journees, calendrier = []) {
  return lignes.map((l) => {
    const s = sessionCalendrierPour(l, journees, calendrier);
    return { ...l, calendrierId: s ? String(s.calendrier_id) : '' };
  });
}

/** « du 03/03/2026 au 05/03/2026 » : la période du devis, depuis les journées. */
export function periodeTexte(journees) {
  const jours = journees.flatMap((j) => j.jours).filter(Boolean).sort();
  if (!jours.length) return null;
  const fr = (iso) => iso.split('-').reverse().join('/');
  return jours.length === 1 ? fr(jours[0]) : `du ${fr(jours[0])} au ${fr(jours[jours.length - 1])}`;
}

/**
 * Fusion des participants de plusieurs sources : une adresse ou un nom déjà
 * vus complètent la fiche existante au lieu d'en ouvrir une seconde.
 */
/**
 * Même personne ? Même adresse, ou mêmes mots de nom et prénom dans un ordre
 * quelconque, ou même nom de famille avec un prénom qui est l'initiale ou le
 * début de l'autre : « t.vaxelaire@… » donne « T VAXELAIRE », la feuille dit
 * « Tom VAXELAIRE ».
 */
function memePersonne(a, b) {
  const ea = String(a.email || '').trim().toLowerCase();
  const eb = String(b.email || '').trim().toLowerCase();
  if (ea && eb) return ea === eb;
  const ca = cleNom(a);
  const cb = cleNom(b);
  if (ca && ca === cb) return true;
  const na = normaliser(a.nom);
  const nb = normaliser(b.nom);
  if (!na || na !== nb) return false;
  const pa = normaliser(a.prenom);
  const pb = normaliser(b.prenom);
  return !pa || !pb || pa.startsWith(pb) || pb.startsWith(pa);
}

function fusionnerParticipants(sources) {
  const participants = [];
  for (const p of sources) {
    const nom = String(p.nom || '').trim();
    const prenom = String(p.prenom || '').trim();
    const email = String(p.email || '').trim();
    if (!nom && !email) continue;

    const existant = participants.find((x) => memePersonne(x, { prenom, nom, email }));
    if (existant) {
      if (!existant.email && email) existant.email = email;
      // Le prénom le plus complet l'emporte sur une initiale.
      if (prenom && prenom.length > String(existant.prenom || '').length) existant.prenom = prenom;
      if (!existant.nom && nom) existant.nom = nom;
      if (!existant.civilite && p.civilite) existant.civilite = p.civilite;
      if (!existant.qualite && p.qualite) existant.qualite = p.qualite;
      if (!existant.societe && p.societe) existant.societe = p.societe;
      continue;
    }
    participants.push({
      ...PARTICIPANT_VIDE,
      civilite: p.civilite || '',
      prenom,
      nom,
      qualite: p.qualite || '',
      societe: p.societe || '',
      email,
    });
  }
  return participants;
}

/** La fiche formateur qui porte ce nom (ou cette adresse), sinon null. */
function trouverFicheFormateur(formateur, fiches) {
  if (!formateur) return null;
  const email = String(formateur.email || '').trim().toLowerCase();
  const cle = cleNom(formateur);
  return (
    fiches.find(
      (f) =>
        (email && String(f.email || '').toLowerCase() === email) || (cle && cleNom(f) === cle)
    ) || null
  );
}

/**
 * @param {object}  p
 * @param {object}  [p.extraction]        contacts.extraction_pieces
 * @param {string}  [p.mailPlanification] contacts.mail_planification
 * @param {Array}   p.catalogue           formations (getFormations)
 * @param {Array}   [p.formateurs]        fiches formateurs (getFormateurs), pour
 *                                        rattacher celui que la feuille nomme
 * @param {Array}   [p.calendrier]        sessions du calendrier (getSessions) :
 *                                        une ligne INTER suit celle de sa date
 * @returns {{ lignes, journees, participants, lieu, formateur, manques: string[], vide: boolean }}
 *   `vide` : rien à proposer du tout (ni lecture ni mail).
 *   `manques` : ce qui empêche de poser le dossier sans repasser par l'écran.
 */
export function construireDossier({
  extraction = null,
  mailPlanification = '',
  catalogue = [],
  formateurs = [],
  calendrier = [],
}) {
  const lu = extraction && typeof extraction === 'object' ? extraction : {};
  const formationsLues = Array.isArray(lu.formations) ? lu.formations : [];
  const parCode = new Map(catalogue.map((f) => [f.code, f]));
  const parIntitule = new Map(catalogue.map((f) => [normaliser(f.intitule), f]));

  // Le formateur n'est jamais un stagiaire. Il vient de la feuille de
  // présence (lu.formateur) ou du mail, où sa ligne dit « Formateur » ; les
  // deux se complètent (le nom de la feuille, l'adresse du mail), et sa
  // ligne sort de la liste des participants.
  const duMail = participantsDepuisEmail(mailPlanification);
  const formateurDuMail = duMail.find((p) => RE_QUALITE_FORMATEUR.test(p.qualite || ''));
  const formateurLu =
    (lu.formateur && typeof lu.formateur === 'object' && lu.formateur) || formateurDuMail || null;
  const cleFormateur = formateurLu ? cleNom(formateurLu) : '';
  const emailFormateur =
    String(formateurLu?.email || '').trim() ||
    String(
      (formateurDuMail && formateurDuMail.email) ||
        duMail.find((p) => cleFormateur && cleNom(p) === cleFormateur)?.email ||
        ''
    ).trim();

  const participants = fusionnerParticipants([
    ...(Array.isArray(lu.participants) ? lu.participants : []),
    ...duMail,
  ]).filter(
    (p) =>
      !RE_QUALITE_FORMATEUR.test(p.qualite || '') &&
      !(cleFormateur && cleNom(p) === cleFormateur) &&
      !(emailFormateur && p.email.toLowerCase() === emailFormateur.toLowerCase())
  );

  const fiche = trouverFicheFormateur(
    formateurLu ? { ...formateurLu, email: emailFormateur } : null,
    formateurs
  );
  const formateur = formateurLu
    ? {
        formateurId: fiche ? String(fiche.id) : '',
        formateurs: fiche
          ? ''
          : [formateurLu.civilite, formateurLu.prenom, formateurLu.nom].filter(Boolean).join(' '),
        formateurEmail: emailFormateur || String(fiche?.email || ''),
        fiche,
      }
    : null;

  const lignes = formationsLues.map((f) => {
    const formation =
      (f.code_catalogue && parCode.get(f.code_catalogue)) ||
      parIntitule.get(normaliser(f.intitule)) ||
      null;
    return {
      ...LIGNE_VIDE,
      formationId: formation ? String(formation.id) : '',
      format: f.format || LIGNE_VIDE.format,
      dureeJours: f.duree_jours || formation?.dureeJours || formation?.duree_jours || '',
      nbParticipants: f.nb_participants || participants.length || 1,
      groupeLibelle: f.groupe_libelle || '',
      // Gardé pour l'écran : dire quelle formation n'a pas été reconnue.
      intituleLu: f.intitule || '',
    };
  });

  const journees = formationsLues
    .filter((f) => Array.isArray(f.dates) && f.dates.length)
    .map((f) => ({
      ...JOURNEE_VIDE,
      intitule: f.intitule || '',
      groupeLibelle: f.groupe_libelle || '',
      jours: [...f.dates].filter(Boolean).sort(),
      modalite: String(f.format || '').includes('mixte') ? 'mixte' : String(f.format || '').includes('distanciel') ? 'distanciel' : f.format === 'inter_presentiel' ? 'inter_presentiel' : 'presentiel',
      lieu: f.lieu || '',
      formateurId: formateur?.formateurId || '',
      formateurs: formateur?.formateurs || '',
      formateurEmail: formateur?.formateurEmail || '',
    }));

  const lieu = formationsLues.find((f) => f.lieu)?.lieu || '';

  // La session du calendrier d'une ligne INTER se lit sur la date.
  const lignesRattachees = rattacherSessions(lignes, journees, calendrier);
  lignes.splice(0, lignes.length, ...lignesRattachees);

  const manques = [];
  if (!lignes.length) manques.push('aucune formation vendue lue dans les pièces');
  const nonReconnues = lignes.filter((l) => !l.formationId).map((l) => l.intituleLu);
  if (nonReconnues.length) {
    manques.push(`formation(s) à choisir au catalogue : ${nonReconnues.join(', ')}`);
  }
  if (lignes.some((l) => l.formationId && !(Number(l.dureeJours) > 0))) {
    manques.push('durée en jours manquante sur une formation');
  }
  if (!journees.length) manques.push('aucune date de formation (journées à planifier)');
  if (!participants.length) manques.push('aucun participant (pièces ou mail de planification)');
  // Des noms sans aucune adresse : la feuille a été lue mais pas le mail des
  // élus. Poser le dossier ainsi enverrait le lien d'émargement à personne.
  if (participants.length && !participants.some((p) => p.email)) {
    manques.push(
      'aucune adresse email : collez le mail du client listant les élus (mail de planification)'
    );
  }

  return {
    lignes,
    journees,
    participants,
    lieu,
    formateur,
    manques,
    // Ce qui a été réellement lu, pour que l'écran le dise.
    sources: {
      formations: formationsLues.length,
      nomsPieces: (Array.isArray(lu.participants) ? lu.participants : []).length,
      adressesMail: duMail.length,
      formateurMail: Boolean(formateurDuMail),
      formateurPieces: Boolean(lu.formateur && typeof lu.formateur === 'object'),
    },
    vide: !lignes.length && !journees.length && !participants.length && !formateur,
  };
}

/**
 * Le texte où chercher les élus et le formateur : le mail de planification,
 * et à défaut le message de la demande — on y colle parfois le mail du
 * client. Seules les lignes portant une adresse comptent, le reste du
 * message est ignoré.
 */
export function texteMailPlanification(contact) {
  return [
    contact?.mail_planification ?? contact?.mailPlanification ?? '',
    contact?.message ?? '',
  ]
    .filter(Boolean)
    .join('\n');
}
